import { useState } from "react"
import {Mail, Phone, MapPin, Send, Clock, CheckCircle2} from 'lucide-react'
import SectionHeading from "@/components/SectionHeading"
import InlineEdit from "@/components/InlineEdit"
import { Link } from "react-router-dom"
import { lumi } from "@/lib/lumi"
import toast from "react-hot-toast"

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    entreprise: "",
    message: "",
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await lumi.functions.invoke("notify-admin", {
        method: "POST",
        body: {
          type: "contact",
          data: {
            nom: form.nom,
            email: form.email,
            telephone: form.telephone,
            entreprise: form.entreprise,
            message: form.message,
            adminEmail: "contact@ljj-it.ch",
          },
        },
      })
      setSubmitted(true)
      toast.success("Message envoyé avec succès !")
      setTimeout(() => setSubmitted(false), 5000)
      setForm({ nom: "", email: "", telephone: "", entreprise: "", message: "" })
    } catch (error: any) {
      const errorMessage = error?.data?.error || "Une erreur est survenue lors de l'envoi."
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen pt-24 lg:pt-32">
      {/* Header */}
      <section className="section-padding pb-16">
        <SectionHeading
          badge="Contact"
          title="Parlons de"
          highlight="votre projet"
          description="Une question, un besoin, une demande de devis ? Remplissez le formulaire ci-dessous ou contactez-nous directement."
        />
      </section>

      <section className="section-padding pb-20">
        <div className="grid lg:grid-cols-[1fr,1.2fr] gap-10 lg:gap-16">
          {/* Info */}
          <div>
            <h3 className="text-xl font-bold text-white mb-6">Nos coordonnées</h3>
            <div className="space-y-5 mb-8">
              <a href="mailto:contact@ljj-it.ch" className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors">Email</div>
                  <div className="text-sm text-slate-400">
                    <InlineEdit contentKey="contact_email" defaultValue="contact@ljj-it.ch" className="text-sm text-slate-400" />
                  </div>
                </div>
              </a>
              <a href="tel:+41792489590" className="flex items-start gap-4 group">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors">Téléphone</div>
                  <div className="text-sm text-slate-400">
                    <InlineEdit contentKey="contact_phone" defaultValue="0041 79 248 95 90" className="text-sm text-slate-400" />
                  </div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Adresse</div>
                  <div className="text-sm text-slate-400">
                    <InlineEdit contentKey="contact_address" defaultValue="Lausanne, Suisse" className="text-sm text-slate-400" />
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-white">Horaires</div>
                  <div className="text-sm text-slate-400">
                    <InlineEdit contentKey="contact_hours" defaultValue="Lun - Ven : 9h00 - 18h00" className="text-sm text-slate-400" />
                  </div>
                </div>
              </div>
            </div>

            <div className="card !p-5 bg-gradient-to-br from-amber-500/5 to-orange-500/5 border-amber-500/20">
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-amber-400">Vous êtes déjà client ?</span>{" "}
                Pour une demande technique, privilégiez l'ouverture d'un{" "}
                <Link to="/support" className="text-cyan-400 hover:underline">
                  ticket de support
                </Link>{" "}
                pour un suivi optimal.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="card !p-8">
            {submitted ? (
              <div className="text-center py-12">
                <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Message envoyé !</h3>
                <p className="text-slate-400 text-sm">
                  Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field">Nom complet *</label>
                    <input
                      type="text"
                      required
                      className="input-field"
                      placeholder="Jean Dupont"
                      value={form.nom}
                      onChange={(e) => setForm({ ...form, nom: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label-field">Email *</label>
                    <input
                      type="email"
                      required
                      className="input-field"
                      placeholder="jean@entreprise.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field">Téléphone</label>
                    <input
                      type="tel"
                      className="input-field"
                      placeholder="+41 79 123 45 67"
                      value={form.telephone}
                      onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label-field">Entreprise</label>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Nom de votre société"
                      value={form.entreprise}
                      onChange={(e) => setForm({ ...form, entreprise: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="label-field">Message *</label>
                  <textarea
                    required
                    rows={5}
                    className="input-field resize-none"
                    placeholder="Décrivez votre besoin ou votre question..."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center !py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
